import { Box, Divider, Table, Thead, Tbody, Heading, Checkbox, UnorderedList, ListItem } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import { EmptyTableRow } from 'components/Table';
import EditActionButton from 'containers/Reports/shared/EditActionButton';
import ExportMenu from 'containers/Reports/shared/ExportMenu';
import { ReportsTableDataCell, ReportsTableHeadCell } from 'containers/Reports/shared/ReportsTableCell';
import ReportsTableRow from 'containers/Reports/shared/ReportsTableRow';
import useStickyHeader from 'containers/Reports/shared/useStickyHeader';
import { roles } from 'global/rolesAndPermissions';
import { useUserObject } from 'store/useUserStore';
import { formatDistanceUnit, formatYearMonthDayTime, formatCurrency } from 'utils/format';
import { isDistanceInMiles } from 'utils/helpers';

interface IReportsByJobTableProps {
  data: any;
  checkedTimesheet: string[];
  dateRange: any[];
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>, timeSheetId: string) => void;
  children?: React.ReactNode | undefined;
}

const ReportsByJobTable: React.FC<IReportsByJobTableProps> = ({
  children,
  data,
  dateRange,
  checkedTimesheet,
  handleCheckboxChange,
}) => {
  const { t } = useTranslation(['common', 'reports']);

  const userStore = useUserObject();
  useStickyHeader({ tableData: data });

  const getTimesheetIds = useCallback(() => {
    let timesheetIds = [];

    if (Array.isArray(data?.timesheets) && data.timesheets.length) {
      timesheetIds = data?.timesheets.map((timesheet: any) => timesheet?.id);
    }

    return timesheetIds;
  }, [data]);

  return (
    <div>
      <Divider my="4" />
      <Heading variant="h2" size="md" fontWeight="semibold" mb="4">
        {t('reports:searchResults')}
      </Heading>
      {data?.attributes ? (
        <ExportMenu
          timesheetIds={getTimesheetIds()}
          dateRange={dateRange}
          reportType="byJob"
          attributes={data?.attributes}
          displaySettings={data?.integrations}
        />
      ) : null}
      <ScrollSync>
        <div style={{ position: 'relative' }}>
          <Table display="block" id="js-report-table">
            <ScrollSyncPane group="horizontal">
              <Thead
                display="block"
                position="sticky"
                top="0"
                background="primary.50"
                overflow="auto"
                sx={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  scrollbarWidth: 'none',
                }}
              >
                <ReportsTableRow color="gray.600" mb="0" border="0">
                  <ReportsTableHeadCell width="30px" justifyContent="center" flex="30 0 auto"></ReportsTableHeadCell>
                  <ReportsTableHeadCell>{t('common:name')}</ReportsTableHeadCell>
                  {userStore?.companyInfo?.show_employee_id ? (
                    <ReportsTableHeadCell width="120px">{t('common:userID')}</ReportsTableHeadCell>
                  ) : null}
                  {userStore?.companyInfo?.show_job_code ? (
                    <ReportsTableHeadCell width="250px">{t('common:jobCode')}</ReportsTableHeadCell>
                  ) : null}
                  <ReportsTableHeadCell width="250px">{t('common:jobOrTask')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="200px">{t('common:clockIn/Out')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="120px">{t('common:duration')}</ReportsTableHeadCell>
                  {userStore?.companyInfo?.show_mileage ? (
                    <ReportsTableHeadCell width="140px">{t('common:miles/km')}</ReportsTableHeadCell>
                  ) : null}
                  {userStore?.companyInfo?.show_timesheet_notes && (
                    <ReportsTableHeadCell>{t('common:notes')}</ReportsTableHeadCell>
                  )}
                  {userStore?.userInfo?.roleId === roles.ADMIN || userStore?.permissions?.manageTimesheets ? (
                    <ReportsTableHeadCell width="80px"></ReportsTableHeadCell>
                  ) : null}
                </ReportsTableRow>
              </Thead>
            </ScrollSyncPane>
            <ScrollSyncPane group="horizontal">
              <Tbody display="block" max-width="100%" overflow="auto" mt={2}>
                {/* Timesheets Data */}
                {Array.isArray(data?.timesheets) && data.timesheets.length ? (
                  data.timesheets.map((timesheet: any) => (
                    <ReportsTableRow key={timesheet?.id} minWidth="100%" width="fit-content">
                      <ReportsTableDataCell width="30px" justifyContent="center" flex="30 0 auto">
                        <Checkbox
                          isChecked={checkedTimesheet.includes(timesheet?.id)}
                          onChange={e => handleCheckboxChange(e, timesheet?.id)}
                          colorScheme="primary"
                        />
                      </ReportsTableDataCell>
                      <ReportsTableDataCell>
                        {timesheet?.first_name} {timesheet?.last_name}
                      </ReportsTableDataCell>
                      {userStore?.companyInfo?.show_employee_id ? (
                        <ReportsTableDataCell width="120px">{timesheet?.employee_id}</ReportsTableDataCell>
                      ) : null}

                      {userStore?.companyInfo?.show_job_code ? (
                        <ReportsTableDataCell width="250px">
                          <Box display="flex" flexDirection="column">
                            {timesheet?.job?.job_code || '- '}
                          </Box>
                        </ReportsTableDataCell>
                      ) : null}
                      <ReportsTableDataCell width="250px">
                        <Box display="flex" flexDirection="column">
                          {timesheet?.job?.name || '- '}
                          {timesheet?.task?.name ? <Box>{'/' + timesheet?.task?.name}</Box> : null}
                        </Box>
                      </ReportsTableDataCell>
                      <ReportsTableDataCell width="200px">
                        {timesheet?.clock_in?.local_time
                          ? formatYearMonthDayTime(timesheet?.clock_in?.local_time)
                          : '- '}
                        {' - '}
                        <br />
                        {timesheet?.clock_out?.local_time
                          ? formatYearMonthDayTime(timesheet?.clock_out?.local_time)
                          : ''}
                      </ReportsTableDataCell>
                      <ReportsTableDataCell width="120px">{timesheet?.payrolls?.duration || '- '}</ReportsTableDataCell>
                      {userStore?.companyInfo?.show_mileage ? (
                        <ReportsTableDataCell width="140px">
                          {timesheet?.mileage ? formatDistanceUnit(+timesheet?.mileage) : '- '}
                        </ReportsTableDataCell>
                      ) : null}
                      {userStore?.companyInfo?.show_timesheet_notes ? (
                        <ReportsTableDataCell>{timesheet?.notes}</ReportsTableDataCell>
                      ) : null}
                      <EditActionButton timesheetId={timesheet?.id} withoutTd={false} />
                    </ReportsTableRow>
                  ))
                ) : (
                  <EmptyTableRow />
                )}
              </Tbody>
            </ScrollSyncPane>
          </Table>
          {Array.isArray(data?.timesheets) && data.timesheets.length ? (
            <UnorderedList
              ml="0"
              listStyleType="none"
              color="inherit"
              textAlign="right"
              sx={{
                '& li': { padding: '2', borderBottom: '1px solid', borderColor: 'gray.300', fontWeight: 'semibold' },
              }}
            >
              <ListItem>
                <Box display="inline-grid" gridTemplateColumns="1fr 100px">
                  <span>{t('reports:totalHoursBilled')}:</span>
                  <span> {data?.total?.total_hour}</span>
                </Box>
              </ListItem>
              <ListItem>
                <Box display="inline-grid" gridTemplateColumns="1fr 100px">
                  <span> {isDistanceInMiles() ? t('reports:totalMileage') : t('reports:totalKilometers')}:</span>
                  <span> {data?.total?.total_mileage ? formatDistanceUnit(+data?.total?.total_mileage) : ''}</span>
                </Box>
              </ListItem>
              <ListItem>
                <Box display="inline-grid" gridTemplateColumns="1fr 100px">
                  <span>{t('reports:totalCost')}:</span>
                  <span> {data?.total?.total_cost ? formatCurrency(+data?.total?.total_cost) : ''}</span>
                </Box>
              </ListItem>
              <ListItem>
                <Box display="inline-grid" gridTemplateColumns="1fr 100px">
                  <span>
                    {' '}
                    {isDistanceInMiles() ? t('reports:totalMileageCost') : t('reports:totalKilometersCost')}:
                  </span>
                  <span> {data?.total?.mileage_cost ? formatCurrency(+data?.total?.mileage_cost) : ''}</span>
                </Box>
              </ListItem>
            </UnorderedList>
          ) : null}
        </div>
      </ScrollSync>

      {children}
    </div>
  );
};

export default ReportsByJobTable;
